/**
 * TanStack Query hooks for the admin sender-blocklist (Phase 12.8).
 *
 * Wraps:
 *   GET    /api/v1/admin/spam            -> useAdminSpamBlocks
 *   POST   /api/v1/admin/spam            -> useAddSenderBlock
 *   DELETE /api/v1/admin/spam/{id}       -> useRemoveSenderBlock
 *
 * The list invalidates on add / remove so the table always reflects the
 * latest server state.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import api from "@/lib/api";
import type { SenderBlockCreate, SenderBlockResponse } from "@/types/api";

export const ADMIN_SPAM_QUERY_KEY = ["admin", "spam"] as const;

export function useAdminSpamBlocks(): UseQueryResult<SenderBlockResponse[]> {
  return useQuery({
    queryKey: ADMIN_SPAM_QUERY_KEY,
    queryFn: async () => {
      const response = await api.get<SenderBlockResponse[]>("/admin/spam");
      return response.data;
    },
  });
}

export function useAddSenderBlock(): UseMutationResult<
  SenderBlockResponse,
  Error,
  SenderBlockCreate
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SenderBlockCreate) => {
      const response = await api.post<SenderBlockResponse>(
        "/admin/spam",
        body,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_SPAM_QUERY_KEY });
    },
  });
}

export function useRemoveSenderBlock(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (blockId: string) => {
      await api.delete(`/admin/spam/${blockId}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_SPAM_QUERY_KEY });
    },
  });
}
